import React, { useState, useEffect, useRef } from 'react';

const SnakeGame = ({ onClose }) => {
  const canvasRef = useRef(null);
  const gameLoopRef = useRef(null);
  const directionRef = useRef({ x: 1, y: 0 });
  const nextDirectionRef = useRef({ x: 1, y: 0 });
  
  // Game constants
  const GRID_WIDTH = 10;
  const GRID_HEIGHT = 15;
  const CELL_SIZE = 20;
  const GAME_SPEED = 100; // milliseconds
  
  // Game states
  const [snake, setSnake] = useState([
    { x: 5, y: 7 },
    { x: 4, y: 7 },
    { x: 3, y: 7 }
  ]);
  const [food, setFood] = useState({ x: 8, y: 7 });
  const [direction, setDirection] = useState({ x: 1, y: 0 });
  const [nextDirection, setNextDirection] = useState({ x: 1, y: 0 });
  const [score, setScore] = useState(0);
  const [gameRunning, setGameRunning] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  
  // Generate random food position
  const generateFood = (currentSnake) => {
    let newFood;
    let foodOnSnake = true;
    
    while (foodOnSnake) {
      newFood = {
        x: Math.floor(Math.random() * GRID_WIDTH),
        y: Math.floor(Math.random() * GRID_HEIGHT)
      };
      
      foodOnSnake = currentSnake.some(
        segment => segment.x === newFood.x && segment.y === newFood.y
      );
    }
    
    return newFood;
  };
  
  // Start/restart game
  const startGame = () => {
    const initialSnake = [
      { x: 5, y: 7 },
      { x: 4, y: 7 },
      { x: 3, y: 7 }
    ];
    setSnake(initialSnake);
    setFood(generateFood(initialSnake));
    setDirection({ x: 1, y: 0 });
    setNextDirection({ x: 1, y: 0 });
    directionRef.current = { x: 1, y: 0 };
    nextDirectionRef.current = { x: 1, y: 0 };
    setScore(0);
    setGameRunning(true);
    setGameStarted(true);
  };
  
  // Handle keyboard input
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Handle Escape to close/toggle snake game
      if (e.code === 'Escape') {
        e.preventDefault();
        if (onClose) {
          onClose();
        }
        return;
      }
      
      // Handle start/restart with Space or Enter
      if ((e.code === 'Space' || e.code === 'Enter') && (!gameRunning)) {
        e.preventDefault();
        startGame();
        return;
      }
      
      if (!gameRunning) return;
      
      // Handle directional input with immediate ref updates
      switch (e.code) {
        case 'ArrowUp':
        case 'KeyW':
          if (directionRef.current.y === 0) {
            nextDirectionRef.current = { x: 0, y: -1 };
            setNextDirection({ x: 0, y: -1 });
          }
          e.preventDefault();
          break;
        case 'ArrowDown':
        case 'KeyS':
          if (directionRef.current.y === 0) {
            nextDirectionRef.current = { x: 0, y: 1 };
            setNextDirection({ x: 0, y: 1 });
          }
          e.preventDefault();
          break;
        case 'ArrowLeft':
        case 'KeyA':
          if (directionRef.current.x === 0) {
            nextDirectionRef.current = { x: -1, y: 0 };
            setNextDirection({ x: -1, y: 0 });
          }
          e.preventDefault();
          break;
        case 'ArrowRight':
        case 'KeyD':
          if (directionRef.current.x === 0) {
            nextDirectionRef.current = { x: 1, y: 0 };
            setNextDirection({ x: 1, y: 0 });
          }
          e.preventDefault();
          break;
        default:
          break;
      }
    };
    
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [gameRunning, gameStarted, onClose]);
  
  // Game loop
  useEffect(() => {
    if (!gameRunning) return;
    
    gameLoopRef.current = setInterval(() => {
      setSnake(prevSnake => {
        // Update direction from buffered input
        directionRef.current = nextDirectionRef.current;
        setDirection(nextDirectionRef.current);
        
        const newHead = {
          x: (prevSnake[0].x + nextDirectionRef.current.x + GRID_WIDTH) % GRID_WIDTH,
          y: (prevSnake[0].y + nextDirectionRef.current.y + GRID_HEIGHT) % GRID_HEIGHT
        };
        
        // Check collision with self
        if (prevSnake.some(segment => segment.x === newHead.x && segment.y === newHead.y)) {
          setGameRunning(false);
          return prevSnake;
        }
        
        let newSnake = [newHead, ...prevSnake];
        
        // Check if food eaten
        if (newHead.x === food.x && newHead.y === food.y) {
          setScore(s => s + 10);
          setFood(generateFood(newSnake));
        } else {
          newSnake.pop();
        }
        
        return newSnake;
      });
    }, GAME_SPEED);
    
    return () => clearInterval(gameLoopRef.current);
  }, [gameRunning, food]);
  
  // Canvas rendering
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    
    // Color palette matching website theme
    const colors = {
      background: '#F9FAFB',      // gray-50 (light background)
      grid: '#E5E7EB',            // gray-200 (grid lines)
      snakeHead: '#4F46E5',       // indigo-600 (primary)
      snakeBody: '#818CF8',       // indigo-400 (secondary)
      food: '#DC3545'             // red (accent/status)
    };
    
    // Clear canvas with light background
    ctx.fillStyle = colors.background;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Draw grid
    ctx.strokeStyle = colors.grid;
    ctx.lineWidth = 0.5;
    for (let i = 0; i <= GRID_WIDTH; i++) {
      ctx.beginPath();
      ctx.moveTo(i * CELL_SIZE, 0);
      ctx.lineTo(i * CELL_SIZE, GRID_HEIGHT * CELL_SIZE);
      ctx.stroke();
    }
    for (let i = 0; i <= GRID_HEIGHT; i++) {
      ctx.beginPath();
      ctx.moveTo(0, i * CELL_SIZE);
      ctx.lineTo(GRID_WIDTH * CELL_SIZE, i * CELL_SIZE);
      ctx.stroke();
    }
    
    // Draw snake
    snake.forEach((segment, index) => {
      if (index === 0) {
        // Head - indigo-600 (primary brand color)
        ctx.fillStyle = colors.snakeHead;
      } else {
        // Body - indigo-400 (lighter secondary)
        ctx.fillStyle = colors.snakeBody;
      }
      ctx.fillRect(
        segment.x * CELL_SIZE + 1,
        segment.y * CELL_SIZE + 1,
        CELL_SIZE - 2,
        CELL_SIZE - 2
      );
    });
    
    // Draw food
    ctx.fillStyle = colors.food;
    ctx.beginPath();
    ctx.arc(
      food.x * CELL_SIZE + CELL_SIZE / 2,
      food.y * CELL_SIZE + CELL_SIZE / 2,
      CELL_SIZE / 2 - 2,
      0,
      Math.PI * 2
    );
    ctx.fill();
    
  }, [snake, food]);
  
  return (
    <div className="flex flex-col items-center gap-3 mb-4 w-full">
      {/* Game Container */}
      <div className="flex flex-col items-center gap-2">
        {/* Canvas */}
        <canvas
          ref={canvasRef}
          width={GRID_WIDTH * CELL_SIZE}
          height={GRID_HEIGHT * CELL_SIZE}
          className="border-4 border-indigo-600 shadow-lg"
          style={{ imageRendering: 'pixelated' }}
        />
        
        {/* Score - Always Visible */}
        <div
          className="text-center text-lg font-bold"
          style={{
            color: '#4F46E5',
            fontFamily: 'Courier New, monospace',
            letterSpacing: '2px',
            minHeight: '28px'
          }}
        >
          SCORE: {score}
        </div>
        
        {/* Controls - Always Visible */}
        <div className="text-center min-h-14 flex items-center justify-center w-full">
          {!gameStarted ? (
            <button
              onClick={startGame}
              className="px-5 py-2 bg-indigo-600 text-white font-bold text-sm border-2 border-indigo-700 hover:bg-indigo-700 transition-colors rounded"
              style={{
                fontFamily: 'Arial Black, sans-serif',
                letterSpacing: '1px'
              }}
            >
              PRESS SPACE TO START
            </button>
          ) : !gameRunning ? (
            <button
              onClick={startGame}
              className="px-5 py-2 bg-indigo-600 text-white font-bold text-sm border-2 border-indigo-700 hover:bg-indigo-700 transition-colors rounded"
              style={{
                fontFamily: 'Arial Black, sans-serif',
                letterSpacing: '1px'
              }}
            >
              GAME OVER - SPACE TO RESTART
            </button>
          ) : (
            <div className="text-gray-600 text-sm font-medium">
              Use Arrow Keys or WASD to Move
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SnakeGame;
